import { PrismicRichText } from '@prismicio/react';
import Image from 'next/image';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fluidProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import { transformProperties, fieldHasContent } from '@/src/domains/prismic/helpers';
import {
  tabletBreakpoint,
  fromDesktopBreakpoint,
  desktopBreakpoint,
} from '@/src/domains/shared/breakpoints';
import BgIllustration from '@/src/views/BenefitsDirectory/assets/start-illustration.png';
import LeftImage from '@/src/views/BenefitsDirectory/assets/start-left-image.svg';
import RightImage from '@/src/views/BenefitsDirectory/assets/start-right-image.svg';
import {
  Title as TitleComponent,
  Description as DescriptionComponent,
} from '@/src/views/CountryBenefits/ContentSlice';

export const ShortDescription = css`
  ${fluidProp('max-width', '200px', '700px')}
`;

export const Section = styled.section`
  --primaryColor: #5700ff;

  ${fluidProp(['padding-top', 'padding-bottom'], '70px', '120px')}
  font-family: var(--typography-fontBrand);
  background: rgba(214, 197, 255, 0.2);
  position: relative;

  ${ContentContainer} {
    position: relative;
    z-index: 1;
  }
`;

const IconWrapper = styled.div`
  width: 35px;

  ${tabletBreakpoint(css`
    margin-bottom: 30px;
  `)};
`;

const LeftIllustrationWrapper = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  z-index: 0;

  ${tabletBreakpoint(css`
    display: none;
  `)};
`;

const RightIllustrationWrapper = styled(LeftIllustrationWrapper)`
  left: unset;
  right: 0;
`;

const BgIllustrationWrapper = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 40%;
  max-width: 400px;
  display: flex;

  ${desktopBreakpoint(css`
    display: none;
  `)};
`;

export const Title = styled(TitleComponent)`
  ${fluidProp('font-size', '28px', '42px')}
  font-family: var(--typography-fontBossa);
  margin-bottom: 28px;
`;

export const Description = styled(DescriptionComponent)`
  && {
    padding-top: 0;

    > p {
      margin-bottom: 20px;

      > strong {
        color: var(--primaryColor);
      }
    }
  }
`;

const FieldTitle = styled.div`
  ${({ theme }) => theme.typography.titleXS};
  color: var(--primaryColor);
  margin: 10px 0;

  > * {
    font-weight: inherit;
    margin: inherit;
  }
`;

const Timeline = styled.div`
  margin-bottom: 29px;

  &:last-child {
    ${FieldTitle}, ${Description} {
      ${fromDesktopBreakpoint(css`
        ${ShortDescription}
      `)};
    }
  }
`;

const Note = styled(DescriptionComponent)`
  ${fromDesktopBreakpoint(css`
    ${ShortDescription}
  `)};
`;

export function BenefitsIntroSlice({ slice }) {
  const { primary } = transformProperties(slice);

  const { title, description, note } = primary;
  const { fields } = slice;

  return (
    <Section>
      <ContentContainer>
        {fieldHasContent(title) && (
          <Title>
            <PrismicRichText field={title} />
          </Title>
        )}
        {fieldHasContent(description) && (
          <Description>
            <PrismicRichText field={description} />
          </Description>
        )}
        <div>
          {fields.map(({ title: fieldTitle, description: fieldDescription, icon }, index) => (
            <Timeline key={index}>
              <IconWrapper>
                <Image
                  src={icon?.url}
                  alt={icon?.alt || ''}
                  width={icon?.dimensions.width}
                  height={icon?.dimensions.height}
                  quality="100"
                />
              </IconWrapper>
              {fieldHasContent(fieldTitle) && (
                <FieldTitle>
                  <PrismicRichText field={fieldTitle} />
                </FieldTitle>
              )}
              {fieldHasContent(fieldDescription) && (
                <Description>
                  <PrismicRichText field={fieldDescription} />
                </Description>
              )}
            </Timeline>
          ))}
        </div>
        {fieldHasContent(note) && (
          <Note>
            <PrismicRichText field={note} />
          </Note>
        )}
      </ContentContainer>
      <LeftIllustrationWrapper>
        <LeftImage />
      </LeftIllustrationWrapper>
      <RightIllustrationWrapper>
        <RightImage />
      </RightIllustrationWrapper>
      <BgIllustrationWrapper>
        <Image
          src={BgIllustration.src}
          alt={BgIllustration.alt || ''}
          width={BgIllustration.width}
          height={BgIllustration.height}
          quality="100"
        />
      </BgIllustrationWrapper>
    </Section>
  );
}
